import { useModal } from '@ebay/nice-modal-react';

import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import Alert from 'components/global/Alert';
import SelectButton from 'components/global/SelectButton';
import Typography from 'components/global/Typography';

import { ACCOUNT_TYPES, KYC_STATUS } from 'utils/constants';
import PATHS from 'utils/paths';

import * as S from '../OTOLink.styled';

const PaymentMethodTab = () => {
	const { invoiceType, kycRegisterStatus, BankIban } = useSelector((state) => state.user.generalSettings);
	const modal = useModal();
	const { t } = useTranslation();
	const {
		setValue,
		watch,
		clearErrors,
		formState: { errors }
	} = useFormContext();

	const onPaymentMethodChange = (value) => {
		const currentValues = watch('paymentMethod') || [];
		clearErrors('paymentMethod');
		setValue(
			'paymentMethod',
			currentValues.includes(value)
				? currentValues.filter((method) => method !== value)
				: [...currentValues, value],
			{
				shouldValidate: true,
				shouldDirty: true
			}
		);
	};

	const PAYMENT_METHODS = useMemo(
		() => [
			{
				id: 'COD',
				image: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/OTO-link/COD-icon.svg',
				title: 'cod'
			},
			{
				id: 'CC',
				image: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/OTO-link/CC-icon.svg',
				title: 'creditCard',
				disabled:
					invoiceType === ACCOUNT_TYPES.PERSONAL ||
					![KYC_STATUS.PENDING, KYC_STATUS.ACTIVE].includes(kycRegisterStatus) ||
					!BankIban
			}
		],
		[invoiceType]
	);

	return (
		<>
			{invoiceType === ACCOUNT_TYPES.PERSONAL && (
				<Alert showIcon className="mt-4">
					<Typography fontWeight="500" color="var(--alert-description)" noWrap>
						<Trans
							i18nKey="otoLinkAccountAlert"
							components={{ linkViewer: <Link to={PATHS.GENERAL_SETTINGS} onClick={modal.remove} /> }}
						/>
					</Typography>
				</Alert>
			)}

			{invoiceType !== ACCOUNT_TYPES.PERSONAL &&
				(!kycRegisterStatus ? (
					<Alert variant="warning" showIcon className="mt-4">
						<Typography fontWeight="500" color="var(--alert-description)">
							addYourDocumentsForCreditCardPayments
						</Typography>
					</Alert>
				) : (
					![KYC_STATUS.PENDING, KYC_STATUS.ACTIVE].includes(kycRegisterStatus) && (
						<Alert variant="warning" showIcon className="mt-4">
							<Typography fontWeight="500" color="var(--alert-description)">
								<Trans
									i18nKey="paymentMethodKYCStatusAlert"
									components={{ strong: <strong /> }}
									values={{ status: t(`status.${kycRegisterStatus}`) }}
								/>
							</Typography>
						</Alert>
					)
				))}

			{!BankIban && (
				<Alert variant="warning" showIcon className="mt-4">
					<Typography fontWeight="500" color="var(--alert-description)">
						<Trans
							i18nKey="otoLinkAddYourBankInformations"
							components={{ linkViewer: <Link to={PATHS.GENERAL_SETTINGS} onClick={modal.remove} /> }}
						/>
					</Typography>
				</Alert>
			)}

			<Alert variant="info" showIcon content="otoLinkSettingsPaymentMethodAlert" className="my-4" />

			<S.PickupOptionsButtonContainer>
				{PAYMENT_METHODS?.map((method) => (
					<SelectButton
						key={method.id}
						checked={watch('paymentMethod')?.includes(method.id)}
						onClick={() => onPaymentMethodChange(method.id)}
						image={method.image}
						title={method.title}
						disabled={method.disabled}
						error={!!errors.paymentMethod}
					/>
				))}
			</S.PickupOptionsButtonContainer>
		</>
	);
};

export default PaymentMethodTab;
